/**
 * Risk Drivers List
 * ==================
 * Displays top 5 risk drivers with severity indicators and evidence.
 * Shows what's contributing most to the risk score.
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  ShieldAlert,
  ChevronRight,
} from 'lucide-react';

// Severity configuration
const SEVERITY_CONFIG = {
  critical: {
    icon: ShieldAlert,
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.1)',
    border: 'rgba(239, 68, 68, 0.2)',
  },
  high: {
    icon: AlertTriangle,
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.08)',
    border: 'rgba(239, 68, 68, 0.15)',
  },
  medium: {
    icon: AlertCircle,
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.15)',
  },
  low: {
    icon: CheckCircle,
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.08)',
    border: 'rgba(16, 185, 129, 0.15)',
  },
  info: {
    icon: Info,
    color: '#6b7280',
    bg: 'rgba(107, 114, 128, 0.08)',
    border: 'rgba(107, 114, 128, 0.15)',
  },
};

// Category badge colors
const CATEGORY_COLORS = {
  customer: '#10b981',
  geographic: '#3b82f6',
  channel: '#f59e0b',
  product: '#8b5cf6',
};

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || '#6b7280';

  return (
    <span style={{
      fontSize: '9px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '2px 6px',
      borderRadius: '4px',
      background: `${color}15`,
      color: color,
    }}>
      {category}
    </span>
  );
}

function DriverItem({ driver, index }) {
  const config = SEVERITY_CONFIG[driver.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '10px 12px',
      background: config.bg,
      borderRadius: '8px',
      border: `1px solid ${config.border}`,
    }}>
      {/* Index + Icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--text-muted)',
          width: '16px',
        }}>
          {index + 1}.
        </span>
        <Icon size={14} style={{ color: config.color }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: driver.evidence ? '4px' : 0,
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 500,
            color: config.color,
          }}>
            {driver.factor}
          </span>
          <CategoryBadge category={driver.category} />
        </div>

        {driver.evidence && (
          <div style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {driver.evidence}
          </div>
        )}
      </div>

      {/* Impact */}
      {driver.impact > 0 && (
        <div style={{
          fontSize: '12px',
          fontWeight: 600,
          color: config.color,
          flexShrink: 0,
        }}>
          +{driver.impact}
        </div>
      )}
    </div>
  );
}

export function RiskDriversList({ drivers = [], maxItems = 5 }) {
  // Filter and sort by impact
  const topDrivers = drivers
    .filter(d => d.severity !== 'info' || d.impact > 0)
    .slice(0, maxItems);

  if (topDrivers.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}>
          <ChevronRight size={14} style={{ color: 'var(--accent-primary)' }} />
          <span style={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
          }}>
            Top Risk Drivers
          </span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: 'rgba(16, 185, 129, 0.08)',
          borderRadius: '8px',
        }}>
          <CheckCircle size={16} style={{ color: '#10b981' }} />
          <span style={{ fontSize: '13px', color: '#10b981' }}>
            No significant risk factors detected
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <ChevronRight size={14} style={{ color: 'var(--accent-primary)' }} />
        <span style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
        }}>
          Top Risk Drivers
        </span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {topDrivers.map((driver, idx) => (
          <DriverItem key={idx} driver={driver} index={idx} />
        ))}
      </div>
    </div>
  );
}

export default RiskDriversList;
